<script setup>
import {computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useStore} from 'vuex';
import {useI18n} from 'vue-i18n';
import axios from '@/axios.config.js';
import ChatMessage from '@/components/order-chat/ChatMessage.vue';
import ReplyPreview from '@/components/order-chat/ReplyPreview.vue';
import {buildReplyPreview} from '@/components/order-chat/replyPreview.js';
import {VueFileAgent} from '@boindil/vue-file-agent-next';
import { Fancybox } from '@fancyapps/ui';
import {log} from "@/utils/clientLogger.js";

const {t} = useI18n();
const store = useStore();
const $swal = inject('$swal');
const chatSyncChannel = new BroadcastChannel('chat_sync_channel');

const socket = inject('$socket');
if (!socket) {
  log.warn('View:ChatPage:$socket', {
    'message': 'WebSocket instance ($socket) is not provided! Chat functionality may not work.'
  });
}

const props = defineProps({
  orderId: {
    type: String,
    required: true,
  },
});

const chatContainerRef = ref(null);
const newMessage = ref('');
const actionState = ref({ type: null, message: null });
const profilePicRef = ref(null);

function handleReply(msgData) {
  actionState.value = { type: 'reply', message: msgData };
  newMessage.value = '';
  document.querySelector('textarea[name="text"]')?.focus();
}

/**
 * Старі повідомлення зберігають у replyTo лише {_id, text, user}, без прев'ю.
 * Для них дістаємо вкладення з оригінального повідомлення, яке вже є в списку.
 */
function resolveReply(replyTo) {
  if (!replyTo) return null;
  if (replyTo.preview) return replyTo;

  const preview = buildReplyPreview(messagesById.value.get(String(replyTo._id)));

  return preview ? { ...replyTo, preview } : replyTo;
}

function handleEdit(msgData) {
  actionState.value = { type: 'edit', message: msgData };
  newMessage.value = msgData.msg;
  document.querySelector('textarea[name="text"]')?.focus();
}

function handleReact(data) {
  if (!isRoomJoined.value || !currentRoomId.value) return;
  socket.emit('messages:react', {
    roomId: currentRoomId.value,
    messageId: data.id,
    emoji: data.emoji
  });
}

function cancelAction() {
  actionState.value = { type: null, message: null };
  newMessage.value = '';
}

const newMedia = ref([]);
const fileRecords = ref([]);
const fileAgentKey = ref(0);
const showAttachmentsModal = ref(false);

const chatAttachments = computed(() => {
  let atts = [];
  messages.value.forEach(msg => {
    if (msg.media && msg.media.length > 0) {
      msg.media.forEach(m => atts.push({ file: m, msg: msg }));
    } else if (msg.image) {
      atts.push({ file: msg.image, msg: msg });
    }
  });
  return atts.reverse();
});

function formatAttTime(timeObj) {
  if (!timeObj) return '';
  const d = new Date(timeObj);
  return d.toLocaleDateString('uk-UA') + ' ' + d.toLocaleTimeString('uk-UA', {hour: '2-digit', minute:'2-digit'});
}

const messages = ref([]);
const messagesById = computed(() => {
  const map = new Map();
  messages.value.forEach(m => {
    if (m._id) map.set(String(m._id), m);
    if (m.tempId) map.set(String(m.tempId), m);
  });
  return map;
});
const usersOfRoom = ref([store.getters.userData]);

const userInfo = computed(() => store.getters.userData);
const isRoomJoined = ref(false);
const currentRoomId = ref(null);

const isRecording = ref(false);
let isRecordingCanceled = false;
const isMuted = ref(localStorage.getItem(`mute_chat_${props.orderId}`) === 'true');

function toggleMute() {
  isMuted.value = !isMuted.value;
  localStorage.setItem(`mute_chat_${props.orderId}`, isMuted.value);
  log.info('View:ChatPage:toggleMute', { isMuted: isMuted.value, orderId: props.orderId });
}

function playNotificationSound() {
  if (isMuted.value) return;

  try {
    const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3");
    audio.volume = 0.75;
    audio.play().catch(e => log.warn('ChatPage:playSound:BLOCK', { error: e.message }));
  } catch (error) {
    log.error('ChatPage:playSound:FATAL', { error });
  }
}

let originalTitle = document.title;
let titleInterval = null;

function alertTab() {
  if (document.hidden) {
    if (titleInterval) clearInterval(titleInterval);

    let isFlashed = false;
    titleInterval = setInterval(() => {
      document.title = isFlashed ? originalTitle : `💬 Нове повідомлення!`;
      isFlashed = !isFlashed;
    }, 1000);

    window.addEventListener('focus', function onFocus() {
      if (titleInterval) clearInterval(titleInterval);
      document.title = originalTitle;
      window.removeEventListener('focus', onFocus);
    }, { once: true });
  }
}

chatSyncChannel.onmessage = (event) => {
  if (event.data.type === 'HANDLED' && event.data.orderId === String(props.orderId)) {
    if (titleInterval) {
      clearInterval(titleInterval);
      document.title = originalTitle;
    }
  }
};

const mediaRecorder = ref(null);
const mediaStream = ref(null);
const audioChunks = ref([]);

const unreadMessages = computed(() => {
  return messages.value.filter(msg => {
    if (msg.user._id === userInfo.value._id) return false;

    if (msg.readBy && msg.readBy.length === 0) return false;

    const amIReadThis = msg.readBy?.some(u => String(u._id || u) === String(userInfo.value._id));

    return !amIReadThis;
  });
});

const unreadCount = computed(() => unreadMessages.value.length);

const unreadTypes = computed(() => {
  const types = new Set();
  unreadMessages.value.forEach(msg => {
    if (msg.audio) types.add('🎙️');
    else if (msg.image || (msg.media && msg.media.length > 0)) types.add('📷');
    else types.add('💬');
  });
  return Array.from(types).join(' ');
});

function getUnreadText(isShort = false) {
  const count = unreadCount.value;
  const r10 = count % 10;
  const r100 = count % 100;

  if (isShort) {
    if (r100 >= 11 && r100 <= 14) return `${count} нових`;
    if (r10 === 1) return `${count} нове`;
    if (r10 >= 2 && r10 <= 4) return `${count} нові`;
    return `${count} нових`;
  }

  if (r100 >= 11 && r100 <= 14) return `У вас ${count} непрочитаних повідомлень`;
  if (r10 === 1) return `У вас ${count} непрочитане повідомлення`;
  if (r10 >= 2 && r10 <= 4) return `У вас ${count} непрочитані повідомлення`;
  return `У вас ${count} непрочитаних повідомлень`;
}

const isScrolledUp = ref(false);
const isInitialLoadDone = ref(false);

function onChatScroll(e) {
  const { scrollTop, scrollHeight, clientHeight } = e.target;

  const distanceToBottom = scrollHeight - Math.ceil(scrollTop) - clientHeight;
  isScrolledUp.value = distanceToBottom > 10;
}

const firstUnreadMessageId = ref(null);
let dividerClearTimeout = null;

watch(() => unreadMessages.value, (newUnread) => {
  if (!firstUnreadMessageId.value && newUnread.length > 0) {
    firstUnreadMessageId.value = newUnread[0]._id || newUnread[0].tempId;
  }

  if (firstUnreadMessageId.value && newUnread.length === 0) {
    if (!dividerClearTimeout) {
      dividerClearTimeout = setTimeout(() => {
        if (unreadMessages.value.length === 0) {
          firstUnreadMessageId.value = null;
        }
        dividerClearTimeout = null;
      }, 4000);
    }
  }

  if (newUnread.length > 0 && dividerClearTimeout) {
    clearTimeout(dividerClearTimeout);
    dividerClearTimeout = null;
  }
}, { immediate: true, deep: true });

function scrollToFirstUnread() {
  const chatContainer = chatContainerRef.value || document.getElementById('chatMessages');
  const firstUnreadMessage = chatContainer ? chatContainer.querySelector('.is-unread') : document.querySelector('.is-unread');
  if (firstUnreadMessage && chatContainer) {
    chatContainer.scrollTo({
      top: firstUnreadMessage.offsetTop - chatContainer.offsetTop - 20,
      behavior: 'smooth'
    });
  }
}

onMounted(() => {
  nextTick(() => {
    initMessageObserver();

    Fancybox.bind('[data-fancybox="modalGallery"]', {
      infinite: true,
      groupAll: true
    });

    const container = chatContainerRef.value || document.getElementById('chatMessages');
    if (container) {
      Fancybox.bind(container, '[data-fancybox^="chatGallery"]', {
        infinite: true,
        groupAll: true
      });
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && messageObserver) {
      messageObserver.disconnect();
      observeUnreadMessages();
    }
  });

  if (!socket) return;

  log.debug('View:ChatPage:onMounted', {
    message: 'Setting up socket listeners',
    orderId: props.orderId
  });

  socket.on('connect', () => {
    log.info('View:ChatPage:socket:connect', {
      message: 'Socket connected or reconnected',
      socketId: socket.id,
      currentRoom: currentRoomId.value
    });

    if (currentRoomId.value) {
      log.debug('View:ChatPage:socket:connect', { message: 'Re-initializing room...' });
      initRoom(currentRoomId.value);
    } else if (props.orderId && !isRoomJoined.value) {
      log.debug('View:ChatPage:socket:connect', { message: 'Initial connection complete, initializing room...' });
      initRoom(props.orderId);
    }
  });

  socket.on('messages:read_updated', (data) => {
    const currentTime = new Date().toISOString();

    messages.value.forEach((msg) => {
      if (data.messageIds.includes(msg._id)) {
        if (!msg.readBy) msg.readBy = [];

        const alreadyRead = msg.readBy.find(u => String(u._id || u) === String(data.readBy._id));

        if (!alreadyRead) {
          msg.readBy.push({
            ...data.readBy,
            readAt: data.readAt || currentTime
          });
        }
      }
    });
  });

  socket.on('messages:edited', (data) => {
    log.info('View:ChatPage:on:messages_edited', data);
    const index = messages.value.findIndex(m => m._id === data.messageId || m.tempId === data.messageId);
    if (index !== -1) {
      messages.value[index].text = data.newText;
      messages.value[index].editedAt = data.editedAt || new Date().toISOString();
    }
  });

  socket.on('messages:reaction_updated', (data) => {
    const index = messages.value.findIndex(m => m._id === data.messageId || m.tempId === data.messageId);
    if (index !== -1) {
      messages.value[index].reactions = data.reactions;
    }
  });

  socket.on('disconnect', (reason) => {
    log.warn('View:ChatPage:socket:disconnect', {
      message: 'Socket disconnected',
      reason: reason,
      currentRoom: currentRoomId.value
    });
    isRoomJoined.value = false;
  });

  socket.on('chats', handleNewMessage);
  socket.on('updateChatUsersList', handleUsersUpdate);
  socket.on('chat-error', handleChatError);
  socket.on('audio:was_read', handleAudioWasRead);

  if (props.orderId) {
    if (socket.connected) {
      initRoom(props.orderId);
    } else {
      log.info('View:ChatPage:waiting', { message: 'Waiting for socket to connect...' });
    }
  }
});

function handleChatError(error) {
  log.error('View:ChatPage:handleChatError', {
    message: 'Received chat error from server',
    error: error,
    currentRoom: currentRoomId.value
  });

  if (error.tempId) {
    const messageIndex = messages.value.findIndex(m => m.tempId === error.tempId);
    if (messageIndex !== -1) {
      log.warn('View:ChatPage:handleChatError:message_failed', {
        message: 'Marking message as failed',
        tempId: error.tempId
      });

      messages.value[messageIndex].failed = true;
    }
  }

  if (error.message && error.message.includes('Not in room')) {
    log.warn('View:ChatPage:handleChatError:not_in_room', {
      message: 'Error indicates not in room. Showing modal.',
      roomId: error.roomId
    });

    if ($swal) {
      $swal.fire({
        title: t('modal.chatErrorTitle'),
        text: t('modal.chatErrorText'),
        icon: 'error',
        confirmButtonText: t('buttons.reloadPage'),
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.reload();
        }
      });
    } else {
      alert('Помилка чату: стався збій з\'єднання. Будь ласка, перезавантажте сторінку.');
      window.location.reload();
    }
  }
}

watch(
    () => props.orderId,
    async (newId, oldId) => {
      log.debug('View:ChatPage:watch:orderId', {
        message: 'Order ID changed',
        oldId: oldId,
        newId: newId,
        currentRoom: currentRoomId.value
      });

      if (newId && newId !== oldId && newId !== currentRoomId.value) {
        await switchRoom(oldId, newId);
      }
    }
);

async function initRoom(roomId) {
  if (!socket || !roomId) {
    log.warn('View:ChatPage:initRoom:invalid', {
      message: 'Cannot init room',
      hasSocket: !!socket,
      roomId: roomId
    });
    return;
  }

  log.info('View:ChatPage:initRoom:start', {
    message: 'Initializing room',
    roomId: roomId
  });

  try {
    await loadMessages(roomId);
    await joinRoom(roomId);
    currentRoomId.value = roomId;

    log.info('View:ChatPage:initRoom:success', {
      message: 'Room initialized successfully',
      roomId: roomId
    });
  } catch (error) {
    log.error('View:ChatPage:initRoom:error', {
      message: 'Failed to initialize room',
      roomId: roomId,
      error: error
    });
  }
}

async function switchRoom(oldRoomId, newRoomId) {
  if (!socket || !newRoomId) {
    log.warn('View:ChatPage:switchRoom:invalid', {
      message: 'Cannot switch room',
      hasSocket: !!socket,
      oldRoomId: oldRoomId,
      newRoomId: newRoomId
    });
    return;
  }

  log.info('View:ChatPage:switchRoom:start', {
    message: 'Switching rooms',
    from: oldRoomId,
    to: newRoomId
  });

  try {
    if (oldRoomId && currentRoomId.value === oldRoomId) {
      await leaveRoom(oldRoomId);
    }

    await loadMessages(newRoomId);

    await joinRoom(newRoomId);

    currentRoomId.value = newRoomId;

    log.info('View:ChatPage:switchRoom:success', {
      message: 'Successfully switched rooms',
      from: oldRoomId,
      to: newRoomId
    });
  } catch (error) {
    log.error('View:ChatPage:switchRoom:error', {
      message: 'Failed to switch rooms',
      from: oldRoomId,
      to: newRoomId,
      error: error
    });
  }
}

function joinRoom(roomId) {
  return new Promise((resolve, reject) => {
    if (!socket || !roomId) {
      const error = 'Cannot join room: invalid socket or roomId';
      log.warn('View:ChatPage:joinRoom:invalid', {
        message: error,
        hasSocket: !!socket,
        roomId: roomId
      });
      return reject(new Error(error));
    }

    log.debug('View:ChatPage:joinRoom:emit', {
      message: 'Emitting joinRoom event',
      roomId: roomId
    });

    socket.timeout(10000).emit('joinRoom', {roomId}, (err, response) => {
      if (err) {
        log.error('View:ChatPage:joinRoom:timeout_or_error', {
          roomId: roomId,
          error: err.message
        });
        isRoomJoined.value = false;
        reject(new Error('Join room failed or timed out'));
        return;
      }

      if (response?.status === 'ok') {
        log.info('View:ChatPage:joinRoom:success', {
          roomId: roomId,
          message: 'Successfully joined room'
        });
        isRoomJoined.value = true;
        resolve(response);
      } else {
        log.error('View:ChatPage:joinRoom:failed', {
          roomId: roomId,
          response: response,
          message: 'Failed to join room'
        });
        isRoomJoined.value = false;
        reject(new Error(response?.error || 'Failed to join room'));
      }
    });
  });
}

function leaveRoom(roomId) {
  return new Promise((resolve) => {
    if (!socket || !roomId || roomId === 'undefined') {
      log.warn('View:ChatPage:leaveRoom:invalid', {
        message: 'Cannot leave room: invalid socket or roomId',
        hasSocket: !!socket,
        roomId: roomId
      });
      return resolve();
    }

    log.debug('View:ChatPage:leaveRoom:emit', {
      message: 'Emitting leaveRoom event',
      roomId: roomId
    });

    socket.emit('leaveRoom', {roomId});
    isRoomJoined.value = false;

    log.info('View:ChatPage:leaveRoom:success', {
      roomId: roomId,
      message: 'Successfully left room'
    });

    resolve();
  });
}

async function handleNewMessage(data) {
  const msgId = data._id || data.tempId;
  const container = chatContainerRef.value || document.getElementById('chatMessages');

  let isActuallyAtBottom = true;
  if (container) {
    const distanceToBottom = Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight);

    isActuallyAtBottom = distanceToBottom <= 10;
  }

  log.info(`Chat: 📥 Отримано повідомлення [${msgId}]`, {
    isActuallyAtBottom: isActuallyAtBottom,
    distancePx: container ? Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) : 'unknown',
    cachedIsScrolledUp: isScrolledUp.value
  });

  const isOwnMessage = data.user._id === userInfo.value._id;
  const isOwnMessageFromServer = isOwnMessage && data.tempId;

  if (isOwnMessageFromServer) {
    const messageIndex = messages.value.findIndex(m => m.tempId === data.tempId);
    if (messageIndex !== -1) {
      messages.value[messageIndex] = data;
    }
  } else {
    messages.value.push(data);

    if (!isOwnMessage) {
      if (document.hasFocus()) {
        chatSyncChannel.postMessage({ type: 'HANDLED', orderId: String(props.orderId) });
        playNotificationSound();
      } else {
        alertTab();
        playNotificationSound();
      }
    }
  }

  await nextTick();

  if (isOwnMessage) {
    log.info(`Chat: 🚀 Скролимо вниз (Наше повідомлення)`);
    scrollToBottom();
  } else if (isActuallyAtBottom) {
    log.info(`Chat: 🚀 Скролимо вниз (Юзер фізично знаходився в самому низу)`);
    scrollToBottom();
  } else {
    log.warn(`Chat: 🛑 АВТОСКРОЛ БЛОКОВАНО! Юзер читає історію.`);
  }

  observeUnreadMessages();
}

function handleUsersUpdate(users) {
  log.debug('View:ChatPage:handleUsersUpdate', {
    message: 'Users list updated',
    usersCount: users.length,
    currentRoom: currentRoomId.value
  });

  usersOfRoom.value = users.filter(
      (user, index, self) => index === self.findIndex((u) => u.id === user.id)
  );
}

function handleAudioWasRead(data) {
  log.info('View:ChatPage:on:audio_was_read:RECEIVED', { data: data });

  messages.value.forEach((msg, index) => {

    if (msg.audio?._id === data.fileId) {

      if (!msg.audio.readBy) {
        msg.audio.readBy = [];
      }

      const alreadyExists = msg.audio.readBy.find(u => u._id === data.userWhoRead._id);

      if (!alreadyExists) {
        log.info('View:ChatPage:on:audio_was_read:UPDATING_UI', { fileId: data.fileId, messageId: msg._id });

        const newReadBy = [...msg.audio.readBy, data.userWhoRead];

        const newAudio = { ...msg.audio, readBy: newReadBy };

        messages.value[index] = {
          ...msg,
          audio: newAudio
        };
      }
    }
  });
}

async function loadMessages(roomId) {
  if (!roomId) {
    log.warn('View:ChatPage:loadMessages:invalid', {
      message: 'Cannot load messages: invalid roomId',
      roomId: roomId
    });
    messages.value = [];
    return;
  }

  log.debug('View:ChatPage:loadMessages:start', {
    message: 'Loading messages',
    roomId: roomId
  });

  if (messageObserver) messageObserver.disconnect();
  readTimeouts.forEach(timeout => clearTimeout(timeout));
  readTimeouts.clear();

  firstUnreadMessageId.value = null;
  if (dividerClearTimeout) {
    clearTimeout(dividerClearTimeout);
    dividerClearTimeout = null;
  }

  isInitialLoadDone.value = false;

  try {
    const res = await axios.get(`/api/messages/${roomId}`);
    messages.value = Array.isArray(res.data) ? res.data : [];

    log.info('View:ChatPage:loadMessages:success', { count: messages.value.length });

    await nextTick();

    const chatContainer = chatContainerRef.value || document.getElementById('chatMessages');
    const firstUnreadMessage = chatContainer ? chatContainer.querySelector('.is-unread') : document.querySelector('.is-unread');

    if (chatContainer) {
      if (firstUnreadMessage) {
        chatContainer.scrollTo({
          top: firstUnreadMessage.offsetTop - chatContainer.offsetTop - 20,
          behavior: 'auto'
        });
      } else {
        chatContainer.scrollTo({
          top: chatContainer.scrollHeight,
          behavior: 'auto'
        });
      }

      setTimeout(() => {
        const distanceToBottom = Math.abs(chatContainer.scrollHeight - chatContainer.scrollTop - chatContainer.clientHeight);
        isScrolledUp.value = distanceToBottom > 10;

        isInitialLoadDone.value = true;
        observeUnreadMessages();
      }, 150);
    }

    observeUnreadMessages();
  } catch (e) {
    log.error('View:ChatPage:loadMessages:error', {
      message: 'Failed to load messages',
      roomId: roomId,
      error: e.message || e
    });
    messages.value = [];
  }
}

let messageObserver = null;
let readTimeouts = new Map();

function initMessageObserver() {
  const container = chatContainerRef.value || document.getElementById('chatMessages');

  messageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const msgId = entry.target.getAttribute('data-message-id');
      if (!msgId || msgId.startsWith('temp_')) return;

      if (entry.isIntersecting) {
        processMessageAsRead(msgId, entry.target);
      }
    });
  }, {
    root: container,
    threshold: 0.2
  });
}

function processMessageAsRead(msgId, element) {
  if (!msgId || readTimeouts.has(msgId)) return;

  log.info(`Observer: 👀 Бачу повідомлення [${msgId}].`);

  const timeout = setTimeout(() => {
    if (document.hidden) {
      log.warn(`Observer: 🛑 СКАСОВАНО [${msgId}] - вкладку згорнуто`);
      return;
    }

    if (element && element.classList.contains('is-unread')) {
      log.info(`Observer: ✅ ВІДПРАВЛЯЮ MARK_READ для [${msgId}]`);
      socket.emit('messages:mark_read', {
        orderId: props.orderId,
        messageIds: [msgId]
      });
      if (messageObserver) {
        messageObserver.unobserve(element);
      }
    }

    readTimeouts.delete(msgId);
  }, 400);

  readTimeouts.set(msgId, timeout);
}

function observeUnreadMessages() {
  const container = chatContainerRef.value || document.getElementById('chatMessages');
  if (!container) return;

  const unreadElements = container.querySelectorAll('.is-unread');

  if (messageObserver) {
    unreadElements.forEach(el => messageObserver.observe(el));
  }

  const containerRect = container.getBoundingClientRect();

  unreadElements.forEach(el => {
    const elRect = el.getBoundingClientRect();

    const isVisible = (
        elRect.top < containerRect.bottom &&
        elRect.bottom > containerRect.top
    );

    if (isVisible) {
      const msgId = el.getAttribute('data-message-id');
      processMessageAsRead(msgId, el);
    }
  });
}

onBeforeUnmount(() => {
  chatSyncChannel.close();

  Fancybox.destroy();

  if (titleInterval) {
    clearInterval(titleInterval);
    document.title = originalTitle;
  }

  readTimeouts.forEach(timeout => clearTimeout(timeout));
  readTimeouts.clear();

  log.debug('View:ChatPage:onBeforeUnmount', {
    message: 'Component unmounting',
    currentRoom: currentRoomId.value
  });

  if (isRecording.value || mediaStream.value) {
    log.warn('View:ChatPage:beforeUnmount', { message: 'The component is destroyed, recording is forced to stop.' });
    if (mediaRecorder.value && isRecording.value) {
      mediaRecorder.value.stop();
    }
    if (mediaStream.value) {
      mediaStream.value.getTracks().forEach(track => track.stop());
    }
    isRecording.value = false;
    mediaRecorder.value = null;
    mediaStream.value = null;
  }

  if (!socket) return;

  if (currentRoomId.value) {
    leaveRoom(currentRoomId.value);
  }

  socket.off('chats', handleNewMessage);
  socket.off('updateChatUsersList', handleUsersUpdate);
  socket.off('chat-error', handleChatError);

  if (messageObserver) {
    messageObserver.disconnect();
  }
});

async function startRecording() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStream.value = stream;
    mediaRecorder.value = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    audioChunks.value = [];
    isRecordingCanceled = false;

    mediaRecorder.value.ondataavailable = (event) => {
      audioChunks.value.push(event.data);
    };

    mediaRecorder.value.onstop = uploadChatRecording;
    mediaRecorder.value.start();
    isRecording.value = true;

  } catch (err) {
    log.error('View:ChatPage:startRecording', { 'error': 'No microphone access', 'details': err });
    $swal.fire(t('alerts.error'), t('messages.noMicrophoneAccess'), 'error');
  }
}

function stopRecording() {
  if (mediaRecorder.value && isRecording.value) {
    log.info('View:ChatPage:stopRecording', { 'message': 'Stopping recorder.' });
    mediaRecorder.value.stop();
    isRecording.value = false;
  }
}

function cancelRecording() {
  if (mediaRecorder.value && isRecording.value) {
    log.info('View:ChatPage:cancelRecording', { 'message': 'Canceling recorder.' });
    isRecordingCanceled = true;
    mediaRecorder.value.stop();
    isRecording.value = false;
  }
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop());
    mediaStream.value = null;
  }
}

async function uploadChatRecording() {
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop());
    mediaStream.value = null;
  }

  if (isRecordingCanceled) {
    return;
  }

  const audioBlob = new Blob(audioChunks.value, { type: 'audio/webm' });
  const MIN_FILE_SIZE = 1000; // 1KB

  if (audioBlob.size < MIN_FILE_SIZE) {
    log.warn('View:ChatPage:uploadRecording', { 'message': 'The entry is too short', 'size': audioBlob.size });
    $swal.fire(t('alerts.warning'), t('messages.audioRecordTooShort'), 'warning');
    return;
  }

  const audioFile = new File([audioBlob], `chat-voice-${Date.now()}.webm`, { type: 'audio/webm' });
  const formData = new FormData();
  formData.append('file', audioFile);

  try {
    const res = await axios.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: store.getters.userToken,
      },
    });

    const uploaded = res.data;
    log.info('View:ChatPage:uploadRecording:success', { 'file': uploaded });

    sendMessage({ audioId: uploaded._id, text: '' });

  } catch (error) {
    log.error('View:ChatPage:uploadRecording:error', { 'error': error });
    $swal.fire(t('alerts.error'), 'Failed to load audio', 'error');
  }
}

function sendMessage({ audioId = null, text: audioText = null } = {}) {
  const text = audioText !== null ? audioText.trim() : newMessage.value.trim();
  const hasText = !!text;
  const hasMedia = newMedia.value.length > 0;
  const hasAudio = !!audioId;

  if (!socket || (!hasText && !hasMedia && !hasAudio)) return;
  if (!isRoomJoined.value || !currentRoomId.value) return;

  if (actionState.value.type === 'edit') {
    if (!hasText) return;
    const msgId = actionState.value.message.id;
    socket.emit('messages:edit', { roomId: currentRoomId.value, messageId: msgId, newText: text });
    const index = messages.value.findIndex(m => m._id === msgId || m.tempId === msgId);
    if (index !== -1) { messages.value[index].text = text; messages.value[index].editedAt = new Date().toISOString(); }
    cancelAction();
    return;
  }

  const userPayload = { _id: userInfo.value._id, username: userInfo.value.username, role: userInfo.value.role };
  const tempId = Date.now();
  let replyPayload = null;
  if (actionState.value.type === 'reply') {
    replyPayload = {
      _id: actionState.value.message.id,
      text: actionState.value.message.msg,
      user: actionState.value.message.msgUser,
      preview: actionState.value.message.preview || null
    };
  }

  const localMessage = {
    user: userPayload, text: text, createdAt: new Date().toISOString(), tempId: tempId, replyTo: replyPayload,
    readBy: [{ _id: userInfo.value._id, username: userInfo.value.username, readAt: new Date().toISOString() }]
  };
  const payload = { user: userPayload, text: text, tempId: tempId, replyTo: replyPayload };

  if (hasMedia) {
    payload.mediaIds = newMedia.value.map(file => String(file.id));
    localMessage.media = newMedia.value.map(file => ({
      path: file.url.replace(/^\//, ''), type: file.type, _id: file.id
    }));
  }

  if (hasAudio) payload.audioId = String(audioId);

  messages.value.push(localMessage);
  socket.emit('chats', currentRoomId.value, payload);

  if (!hasAudio) {
    newMessage.value = '';
    newMedia.value = [];
    fileRecords.value = [];
    fileAgentKey.value++;
  }

  nextTick(() => { scrollToBottom(); });
  cancelAction();
}

function scrollToBottom() {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'TEXTAREA') {
    return;
  }

  const container = chatContainerRef.value || document.getElementById('chatMessages');
  if (container) {
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }
}

async function filesSelected(filesNewlySelected) {
  const validFiles = filesNewlySelected.filter(record => record.file);
  if (!validFiles.length) return;

  const uploadPromises = validFiles.map(async (fileRecord) => {
    if (fileRecord.file.size > 10 * 1024 * 1024) {
      $swal.fire(t('alerts.error'), `Файл ${fileRecord.file.name} занадто великий. Макс 10 МБ.`, 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', fileRecord.file);

    try {
      const res = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: store.getters.userToken,
        },
      });

      const data = res.data;
      if (data && data._id) {
        const isDuplicate = newMedia.value.some(m => String(m.id) === String(data._id));
        if (!isDuplicate) {
          newMedia.value.push({
            id: data._id,
            type: data.type,
            name: data.fileName,
            url: '/' + data.path,
            size: data.size
          });
        }
      }
    } catch (error) {
      log.error('View:ChatPage:filesSelected:error', { error });
    }
  });

  await Promise.all(uploadPromises);

  fileRecords.value = [];
  fileAgentKey.value++;
}

function removeMediaPreview(index) {
  newMedia.value.splice(index, 1);
}

function handlePaste(event) {
  const clipboardItems = event.clipboardData.items;
  for (let i = 0; i < clipboardItems.length; i++) {
    const item = clipboardItems[i];
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file.size > 10 * 1024 * 1024) {
        $swal.fire(t('alerts.error'), 'Файл занадто великий. Максимальний розмір 10 МБ.', 'error');
        event.preventDefault(); return;
      }
      const formData = new FormData();
      formData.append('file', file);
      axios.post('/api/upload', formData, { headers: { 'Content-Type': 'multipart/form-data', Authorization: store.getters.userToken } })
          .then((res) => {
            newMedia.value.push({
              id: res.data._id, type: res.data.type, name: res.data.fileName,
              url: '/' + res.data.path, size: res.data.size
            });
          }).catch((error) => { log.error('View:ChatPage:handlePaste:error', { error: error }); });
      event.preventDefault(); return;
    }
  }
}
</script>

<template>
  <div id="chatContainer">
    <div class="d-flex justify-content-center w-100 mx-0">
      <div class="wrapper w-100">
        <div class="main pt-0">
          <div class="chat-header bg-white px-3 py-2 d-flex justify-content-between align-items-center border-bottom shadow border border-secondary border-opacity-25" style="z-index: 10; position: relative;">
            <div class="d-flex align-items-center flex-wrap mt-1">
              <span class="text-secondary me-2" style="font-size: 13px; font-weight: 500;">
                {{ t('labels.orderViewedBy') }}
              </span>
              <div class="d-inline-block">
                <span v-for="(user, index) in usersOfRoom" :key="index" class="badge bg-success me-1 mb-1">
                  {{ user.login || user.username }}
                </span>
              </div>
            </div>

            <div class="d-flex align-items-center ms-auto" style="gap: 8px;">
              <div class="position-relative">
                <button class="btn btn-sm d-flex align-items-center justify-content-center text-primary bg-light border-primary border-opacity-50"
                        @click="showAttachmentsModal = true" title="Вкладення"
                        style="width: 34px; height: 34px; padding: 0; border-radius: 8px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                  </svg>
                </button>
                <span v-if="chatAttachments.length > 0" class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style="font-size: 10px;">
                  {{ chatAttachments.length }}
                </span>
              </div>

              <button
                  class="btn btn-sm d-flex align-items-center justify-content-center transition-all bg-light"
                  :class="isMuted ? 'text-danger border-danger' : 'text-secondary border-secondary'"
                  @click="toggleMute"
                  :title="isMuted ? 'Увімкнути звук чату' : 'Вимкнути звук чату'"
                  style="width: 34px; height: 34px; padding: 0; border-radius: 8px; flex-shrink: 0; border-width: 1px; border-style: solid; opacity: 0.9;"
              >
                <svg v-if="isMuted" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M9 3.14a5 5 0 0 1 7.86 4.36v3.23l2 2v1h-3.48"></path>
                  <path d="M21 14.73l-1.25-1.25M17.27 11v-3.5a5.5 5.5 0 0 0-8.21-4M6 8.5v4l-2 2v1h14"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
              </button>
            </div>
          </div>

          <div ref="chatContainerRef" id="chatMessages" class="px-3 pt-2 scroll position-relative" @scroll="onChatScroll">
            <template v-for="message in messages" :key="message._id || message.tempId">

              <transition name="fade">
                <div v-if="(message._id === firstUnreadMessageId || message.tempId === firstUnreadMessageId) && firstUnreadMessageId !== null"
                     class="d-flex justify-content-center align-items-center my-4 w-100 position-relative">

                  <div style="position: absolute; width: 100%; height: 1px; background-color: #0b5ed7; top: 50%; z-index: 1;"></div>

                  <span class="px-3 py-1 rounded-pill shadow-sm" style="font-size: 11px; letter-spacing: 0.5px; background-color: #0b5ed7; color: white; z-index: 2; font-weight: 500;">
                    Непрочитані повідомлення
                  </span>
                </div>
              </transition>

              <ChatMessage
                  :id="message._id || message.tempId"
                  :msgUser="message.user"
                  :msg="message.text"
                  :image="message.image"
                  :media="message.media"
                  :time="message.createdAt"
                  :audio="message.audio"
                  :readBy="message.readBy"
                  :replyTo="resolveReply(message.replyTo)"
                  :editedAt="message.editedAt"
                  :order-id="props.orderId"
                  :reactions="message.reactions"
                  @reply="handleReply"
                  @edit="handleEdit"
                  @react="handleReact"
                  :class="{
                    'is-unread': message.user._id !== userInfo._id &&
                                 message.readBy && message.readBy.length > 0 &&
                                 !message.readBy.some(u => String(u._id || u) === String(userInfo._id))
                  }"
                  :data-message-id="message._id"
              />
            </template>
          </div>

          <div class="position-relative">
            <transition name="fade">
              <div v-if="unreadCount > 0"
                   @click="scrollToFirstUnread"
                   class="w-100 py-2 d-flex justify-content-center align-items-center shadow-sm"
                   style="background-color: #d9edf7; border-top: 1px solid #bce8f1; color: #31708f; font-size: 13px; font-weight: 600; cursor: pointer; z-index: 20;">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="me-2" viewBox="0 0 16 16">
                  <path fill-rule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1"/>
                </svg>
                {{ getUnreadText(false) }} <span v-if="unreadTypes" class="ms-1">{{ unreadTypes }}</span>
              </div>
            </transition>
          </div>
          <transition name="fade">
            <button
                v-if="isScrolledUp"
                @click="scrollToBottom"
                class="btn btn-primary rounded-circle shadow-lg d-flex justify-content-center align-items-center position-absolute"
                style="bottom: 90px; right: 15px; width: 40px; height: 40px; z-index: 1050; opacity: 0.95;"
                title="До нових повідомлень"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="white" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
              </svg>
              <span v-if="unreadCount > 0"
                    class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white"
                    style="font-size: 0.7rem; padding: 0.35em 0.5em;">
                {{ unreadCount }}
              </span>
            </button>
          </transition>

          <div class="bg-white border-top w-100" style="z-index: 20; position: relative;">

            <div v-if="actionState.type" class="w-100 px-3 py-2 d-flex justify-content-between align-items-center gap-2 border-bottom bg-light">
              <ReplyPreview
                  :preview="actionState.type === 'reply' ? actionState.message.preview : null"
                  :title="actionState.type === 'reply'
                    ? t('labels.replyTo', 'Відповідь для') + ' ' + actionState.message.msgUser.username
                    : t('labels.editMessage', 'Редагування повідомлення')"
                  :text="actionState.message.msg"
                  :title-color="actionState.type === 'reply' ? '#0d6efd' : '#ffc107'"
                  border-color="#0dcaf0"
              />
              <button @click="cancelAction" class="btn-close flex-shrink-0" style="font-size: 10px;"></button>
            </div>

            <div v-if="newMedia.length > 0" class="w-100 px-3 py-2 d-flex gap-2 overflow-auto bg-light border-bottom" style="max-height: 100px;">
              <div v-for="(file, index) in newMedia" :key="index" class="position-relative d-inline-block shadow-sm rounded" style="width: 60px; height: 60px; flex-shrink: 0;">
                <video v-if="file.type && file.type.startsWith('video/')" class="w-100 h-100 rounded" style="object-fit: cover;" :src="file.url"></video>
                <img v-else class="w-100 h-100 rounded" style="object-fit: cover;" :src="file.url" />
                <button @click="removeMediaPreview(index)" class="btn btn-sm btn-danger position-absolute rounded-circle d-flex align-items-center justify-content-center" style="top: -5px; right: -5px; width: 18px; height: 18px; padding: 0; font-size: 10px;">✕</button>
              </div>
            </div>

            <div class="row align-items-center justify-content-between w-100 gx-2 mx-0 p-2 border border-secondary border-opacity-25 bg-white">
              <template v-if="!isRecording">
                <div class="col-auto px-1">
                  <VueFileAgent
                      :key="fileAgentKey"
                      v-model="fileRecords"
                      ref="profilePicRef"
                      :auto="false"
                      :multiple="true"
                      :deletable="false"
                      :meta="false"
                      :compact="true"
                      accept="image/*,video/*"
                      helpText=" "
                      :errorText="{ type: t('messages.onlyImageDownload') }"
                      @select="filesSelected"
                  />
                </div>

                <div class="col px-1 flex-grow-1">
                  <textarea
                      v-model="newMessage"
                      @keyup.enter.exact.prevent="sendMessage({})"
                      @paste="handlePaste"
                      name="text"
                      class="form-control w-100 bg-light"
                      :placeholder="t('placeholders.writeMessage')"
                      :disabled="!isRoomJoined"
                      style="resize: none; border-radius: 8px;">
                  </textarea>
                </div>

                <div class="col-auto px-1">
                  <button
                      v-if="newMessage.trim() || newMedia.length > 0"
                      class="btn btn-warning rounded-circle d-flex align-items-center justify-content-center"
                      @click="sendMessage({})"
                      :disabled="!isRoomJoined"
                      style="width: 40px; height: 40px; padding: 0;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M10 14l11 -11" />
                      <path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5" />
                    </svg>
                  </button>
                  <button
                      v-else
                      class="btn btn-success rounded-circle d-flex align-items-center justify-content-center"
                      @click="startRecording"
                      :disabled="!isRoomJoined"
                      style="width: 40px; height: 40px; padding: 0;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M9 2m0 3a3 3 0 0 1 3 -3h0a3 3 0 0 1 3 3v5a3 3 0 0 1 -3 3h0a3 3 0 0 1 -3 -3z" />
                      <path d="M5 10a7 7 0 0 0 14 0" />
                      <path d="M8 21l8 0" />
                      <path d="M12 17l0 4" />
                    </svg>
                  </button>
                </div>
              </template>

              <template v-if="isRecording">
                <div class="col-auto px-1">
                  <button class="btn btn-outline-danger rounded-circle d-flex align-items-center justify-content-center" @click="cancelRecording" title="Скасувати запис" style="width: 40px; height: 40px; padding: 0;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>
                  </button>
                </div>
                <div class="col text-center d-flex align-items-center justify-content-center px-1">
                  <span class="text-danger fw-bold pulse-animation">⏺ {{ t('placeholders.recordingInProgress') }}</span>
                </div>
                <div class="col-auto px-1">
                  <button class="btn btn-warning rounded-circle d-flex align-items-center justify-content-center" @click="stopRecording" style="width: 40px; height: 40px; padding: 0;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 14l11 -11" /><path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5" /></svg>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showAttachmentsModal" class="modal-backdrop fade show" style="background-color: rgba(0,0,0,0.5); z-index: 1050;"></div>
    <div v-if="showAttachmentsModal" class="modal fade show d-block" tabindex="-1" style="z-index: 1055;">
      <div class="modal-dialog modal-dialog-scrollable modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-light">
            <h5 class="modal-title text-primary fw-bold">Вкладення чату</h5>
            <button type="button" class="btn-close" @click="showAttachmentsModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="chatAttachments.length === 0" class="text-center text-muted my-5">
              Немає завантажених фото чи відео.
            </div>
            <div class="row row-cols-3 row-cols-md-4 g-2">
              <div class="col" v-for="(att, idx) in chatAttachments" :key="idx">
                <div class="card h-100 shadow-sm border-0 position-relative">
                  <div style="aspect-ratio: 1; overflow: hidden; background: #f8f9fa;" class="rounded-top position-relative zoom-hover-effect">
                    <video v-if="att.file.type && att.file.type.startsWith('video/')" class="w-100 h-100" style="object-fit: cover;" :src="'/' + att.file.path" controls></video>
                    <template v-else>
                      <img :src="'/' + att.file.path" class="w-100 h-100" style="object-fit: cover; cursor: pointer;" :data-fancybox="'modalGallery'" />
                      <div class="hover-overlay d-flex align-items-center justify-content-center position-absolute top-0 start-0 w-100 h-100" style="pointer-events: none;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          <line x1="11" y1="8" x2="11" y2="14"></line>
                          <line x1="8" y1="11" x2="14" y2="11"></line>
                        </svg>
                      </div>
                    </template>
                  </div>
                  <div class="card-footer p-1 text-center bg-white" style="font-size: 10px;">
                    <strong class="text-truncate d-block">{{ att.msg.user?.username || 'Користувач' }}</strong>
                    <span class="text-muted">{{ formatAttTime(att.msg.createdAt) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.main {
  background-color: #eee;
  width: 100%;
  position: relative;
  padding: 6px 0 0 0;
}

.scroll {
  overflow-y: scroll;
  height: 60vh;
  padding-bottom: 20px;
}

.navbar {
  border: 1px solid rgba(51, 51, 51, 0.44);
}

.form-control {
  font-size: 13px;
  font-weight: 400;
  height: 40px;
  border: 1px solid #ced4da;
  background-color: #fff !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
  outline: 0;
}

.pulse-animation {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.zoom-hover-effect {
  img {
    transition: transform 0.3s ease;
  }
  .hover-overlay {
    background: rgba(0, 0, 0, 0.4);
    opacity: 0;
    transition: opacity 0.3s ease;

    svg {
      transform: scale(0.5);
      transition: transform 0.3s ease;
    }
  }

  &:hover {
    img {
      transform: scale(1.05);
    }
    .hover-overlay {
      opacity: 1;

      svg {
        transform: scale(1);
      }
    }
  }
}
</style>

<style lang="scss">
#chatContainer {
  .vue-file-agent.file-input-wrapper {
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    min-height: 40px !important;
  }

  .grid-block.file-preview {
    display: none !important;
  }

  .grid-block.file-preview-new {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    margin: 0 !important;
    padding: 0 !important;
    border-radius: 50% !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    transition: all 0.2s ease;

    &:hover {
      background: #e9ecef !important;
    }
  }

  /* Бібліотека малює власний прямокутний оверлей у ::before/::after —
     без цього фікса під круглою кнопкою видно сірий квадрат. */
  .grid-block.file-preview-new::before,
  .grid-block.file-preview-new::after {
    border-radius: 50% !important;
  }

  .grid-block.file-preview-new .help-text {
    display: none !important;
  }

  .grid-block.file-preview-new .file-preview-wrapper {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .grid-block.file-preview-new svg {
    width: 20px !important;
    height: 20px !important;
    fill: #6c757d !important;
  }

  .grid-block-wrapper .grid-block.grid-box-item {
    display: none !important;
  }

  .grid-block-wrapper .grid-block.file-preview-new {
    display: flex !important;
  }
}

/* Стилі обмежені контейнером чату, щоб не протікати глобально
   (інакше .form-control z-index:100 перекриває інші елементи, напр. селект клієнта). */
#chatContainer {
  textarea, input {
    -webkit-user-select: text !important;
    user-select: text !important;
    -webkit-touch-callout: default !important;
  }

  .form-control {
    z-index: 100;
    position: relative;
  }
}
</style>

